// "What is this?" on each parameter card. Three questions, in this order
// (docs/GUIDED-EXPERIENCE-PLAN.md, Stage A): what is it, how did it get
// in my tank, what happens next. The third depends on the tank's phase,
// because the same number means opposite things: ammonia in week two of a
// cycle is the cycle working, ammonia in a mature tank means something
// died.
//
// Truth rules (owner, 2026-09-26: "make sure that all are true"):
//   - No tank-specific numbers in the copy. The card shows the keeper's
//     own range next to it, from the tank's params, so the words can never
//     disagree with what the app scores against.
//   - The few general numbers used (seawater salinity, the dose
//     calculator's 1.4 dKH/day correction cap) are the ones the code
//     already cites: Holmes-Farley 2004 in db/seeds/index.js, and
//     DAILY_RISE_CAPS in dose-calc.js.
//   - A claim about practice says it is practice ("many reef keepers").
//   - No guilt, no em dashes (docs/ANTI-PATTERNS.md #13).
//   - Every sentence was checked against published sources before it
//     shipped (2026-09-26). The review's corrections are why the nitrogen
//     cycle says "microbes" (archaea do much of the ammonia oxidation in
//     aquarium filters, Sauder et al. 2011), why reef nitrite is not
//     "always zero" (denitrification makes some, Holmes-Farley 2005), and
//     why the pale-coral lines are stated as keepers' experience.
//
// The live copy is the admin-editable `param_explainers` config; this is
// its default and the app's offline fallback.
//
// Fields per item:
//   key      the tank param key (alk, calcium, ... kh, gh, tds)
//   only     optional 'fresh' | 'salt': the water type it applies to. A
//            reefer's custom "kh" means alkalinity, not freshwater KH.
//   what     what it is
//   source   how it gets into (and out of) the tank
//   next     what happens next, by phase: setup, cycling, maturing,
//            growing, established, and `default` for anything else
//   fresh    optional overrides of what / source / next for freshwater
//            tanks (the base copy is saltwater)

export const FRESHWATER_SUBTYPES = [
  'planted-high',
  'planted-low',
  'community',
  'cichlid',
  'shrimp',
  'other-freshwater',
];

export const PHASES = ['setup', 'cycling', 'maturing', 'growing', 'established'];

// The nitrogen cycle reads the same in fresh and salt water, so a
// freshwater tank keeps the base setup / cycling copy unless `fresh.next`
// overrides it. Every later phase comes only from `fresh.next` (or, for an
// item with none, the base `default`, which is then water-neutral), because
// the reef phases (maturing, growing) describe corals and coralline
// algae, and score.js still gives some freshwater subtypes those phases.
const SHARED_PHASES = ['setup', 'cycling'];

export const DEFAULT_PARAM_EXPLAINERS = {
  items: [
    {
      key: 'ammonia',
      what: 'Ammonia is the first waste product in the tank. Even small amounts can harm fish and other animals, and more so at higher pH and temperature, because more of it is then in its more toxic form.',
      source:
        'Fish give it off, and it is released as uneaten food, waste and anything dead break down. In a new tank it can also come from an ammonia source or food added to start the cycle.',
      next: {
        cycling:
          'Ammonia now is expected. It is the first step of the cycle: microbes that feed on it grow in the rock and filter and turn it into nitrite, and it falls as they catch up. The usual advice is to wait until ammonia and nitrite both read zero, or very close to it, before adding anything alive.',
        default:
          'Once a tank has finished cycling, ammonia should read zero or close to it. If it shows up after that, waste is usually arriving faster than the microbes can handle it: something dead in the tank, a lot of uneaten food, or filter media cleaned or replaced all at once. Retest to rule out the kit, then look for the cause. If your tank is still cycling, ammonia now is expected.',
      },
    },
    {
      key: 'nitrite',
      what: 'Nitrite is the middle step of the nitrogen cycle. Microbes make it from ammonia, and others turn it into nitrate.',
      source:
        'It is mostly made inside the tank from ammonia. In a reef, low-oxygen spots such as deep sand beds and denitrators can also make a little from nitrate.',
      next: {
        cycling:
          'Nitrite rising after ammonia means the cycle is moving forward. When ammonia and nitrite have both fallen back to zero or very close to it, and nitrate is showing, the readings look like a finished cycle. Check that they hold on another test a day or more later.',
        default:
          'Once a tank has finished cycling, nitrite usually reads zero or close to it. Saltwater fish tolerate nitrite far better than freshwater fish, because the chloride in seawater competes with it at the gills. A reading that keeps rising can still mean the tank is behind on waste: too much at once, or a setback from heavy cleaning or some medications. If your tank is still cycling, nitrite now is expected.',
      },
      fresh: {
        source:
          'It is mostly made inside the tank from ammonia. Some tap water carries a little too.',
        next: {
          default:
            'Once a tank has finished cycling, nitrite should read zero. It is toxic to freshwater fish even at low levels, so a reading after the cycle is worth acting on. It usually means the tank is behind on waste, from too much at once or a setback from heavy cleaning or some medications. Retest to rule out the kit, and a water change lowers it while you find the cause. If your tank is still cycling, nitrite now is expected.',
        },
      },
    },
    {
      key: 'nitrate',
      what: 'Nitrate is the end product of the nitrogen cycle. It is far less harmful than ammonia or nitrite, and in a reef a small amount feeds corals and the algae that live inside them.',
      source:
        'Microbes make it from nitrite, so much of the nitrogen in food ends up as nitrate. Water changes, algae and a refugium take it out, and a protein skimmer removes some waste before it can become nitrate. Some reef keepers add it on purpose when it runs too low.',
      next: {
        cycling:
          'Nitrate appearing is the last step of the cycle. Once ammonia and nitrite read zero or very close to it, and nitrate is above zero, the readings look like a finished cycle; check that they hold on another test a day or more later. A high number at the end of a cycle is normal, and a water change brings it down before livestock goes in.',
        maturing:
          'Algae often blooms at this stage while nitrate settles. Feed lightly and keep up with water changes rather than chasing the number.',
        default:
          'In a reef, many keepers find that nitrate near zero goes with pale corals, and high nitrate feeds algae. What matters most is that it stays steady inside your range. Fish-only tanks tolerate much more than corals do.',
      },
      fresh: {
        what: 'Nitrate is the end product of the nitrogen cycle. It is far less harmful than ammonia or nitrite, and plants use it as fertiliser.',
        source:
          'Microbes make it from nitrite, so much of the nitrogen in food ends up as nitrate. Tap water can carry some in too. Water changes and plants take it out, and many planted-tank keepers add it as a fertiliser.',
        next: {
          default:
            'Regular water changes keep it in your range. In a planted tank the plants use it up, so a reading that keeps falling can mean they need more.',
        },
      },
    },
    {
      key: 'phosphate',
      what: 'Phosphate is a nutrient. Corals need a little, and too much feeds algae and can slow how fast corals build their skeletons.',
      source:
        'Most of it comes from food, which is rich in phosphate. Tap water can carry it too, which is one reason reef keepers use RO/DI water. Some new rock, especially dry rock, can release it for a while. Water changes, algae and phosphate removers such as GFO take it out.',
      next: {
        cycling:
          'A high reading in a new tank is common, especially with new rock. It often comes down as the tank matures, and water changes or a phosphate remover help if it does not.',
        maturing:
          'Diatoms and other algae often bloom at this stage and feed on phosphate. This usually passes on its own as the tank settles.',
        default:
          'Corals do best with a small, steady amount. Many reef keepers find phosphate near zero goes with pale corals, and research links phosphate starvation to corals bleaching more easily. High phosphate feeds algae. If it needs to come down, bring it down slowly.',
      },
      fresh: {
        what: 'Phosphate is a plant nutrient. Planted tanks need it, and in a tank without plants extra phosphate mostly feeds algae.',
        source:
          'Most of it comes from fish food. Tap water can carry it too, and planted-tank fertilisers add it on purpose. Water changes and plants take it out.',
        next: {
          cycling:
            'Phosphate from food, tap water and some planted-tank soils can build up while the tank cycles. Water changes bring it down if it runs above your range.',
          default:
            'In a planted tank, keep it in your range so plants have enough. In a tank without plants, water changes and lighter feeding bring it down.',
        },
      },
    },
    {
      key: 'alk',
      only: 'salt',
      what: "Alkalinity measures the water's carbonate and bicarbonate, the buffer that holds pH steady. Corals, clams and coralline algae use it to build their skeletons.",
      source:
        'The salt mix sets it when new water is mixed. Growing corals and coralline algae use it up, so in a tank with stony corals it usually drifts down unless it is dosed. Water changes and dosing (two-part, kalkwasser or a calcium reactor) put it back.',
      next: {
        cycling:
          'The microbes that run the cycle use up a little alkalinity as they work, so a small drop now is normal. There is nothing to dose yet.',
        maturing:
          'With few or no corals yet, alkalinity mostly stays where the salt mix set it. Water changes are usually enough.',
        growing:
          'Once corals are in, they start to use it. Test regularly to learn how fast it drops, and start dosing only when it measurably falls.',
        default:
          "Steady matters more than perfect. Corals handle a stable number inside your range better than swings, so move it slowly. When alkalinity needs raising, the dose calculator here splits the correction so no day's correction adds more than 1.4 dKH.",
      },
    },
    {
      key: 'calcium',
      only: 'salt',
      what: 'Calcium is what corals, clams and coralline algae build their skeletons from, together with alkalinity.',
      source:
        'The salt mix sets it. Coral growth uses it up along with alkalinity, roughly in step. Water changes and dosing put it back.',
      next: {
        cycling: 'Little is being used yet, so calcium mostly stays where the salt mix set it.',
        maturing:
          'With few or no corals yet, calcium mostly stays where the salt mix set it. Water changes are usually enough.',
        default:
          'Calcium moves more slowly than alkalinity, so it needs testing less often. When calcium and alkalinity fall together, corals and other calcifiers are usually using them, and dosing both in balance keeps them in step.',
      },
    },
    {
      key: 'magnesium',
      only: 'salt',
      what: 'Magnesium helps keep calcium and alkalinity dissolved in the water, instead of settling out as solid carbonate on rocks, heaters and pumps. Coralline algae use some of it too.',
      source:
        'The salt mix sets it. It is used slowly, so it drifts over weeks rather than days. Water changes and magnesium supplements put it back.',
      next: {
        cycling: 'Little is being used yet, so magnesium mostly stays where the salt mix set it.',
        maturing:
          'With few or no corals yet, magnesium mostly stays where the salt mix set it. Water changes are usually enough.',
        default:
          'Check it when calcium and alkalinity get hard to hold. Low magnesium makes both harder to keep up. Raise it slowly.',
      },
    },
    {
      key: 'salinity',
      only: 'salt',
      what: 'Salinity is how much salt is dissolved in the water. Natural seawater is about 35 parts per thousand, a specific gravity of about 1.026.',
      source:
        'Only water evaporates, so salinity rises as the tank loses water, and topping up with fresh RO/DI water brings it back. New salt water mixed stronger or weaker than the tank moves it at every water change. Salt creep and skimmer waste take a little salt out over time, and some dosing adds a little.',
      next: {
        setup:
          'Get this right before anything else goes in. Check the new salt water and adjust it until it reads inside your range.',
        default:
          'Steady matters more than the exact number. Top off with fresh water, never salt water, because the salt does not evaporate. An auto top-off keeps it steady day to day. If readings jump, calibrate your refractometer or probe before changing anything in the tank.',
      },
    },
    {
      key: 'temp',
      what: 'Temperature sets the pace of everything in the tank: fish and coral metabolism, the microbes, and how much oxygen the water can hold. Warmer water holds less oxygen.',
      source:
        'Heaters, lights, pumps and the room around the tank all add or remove heat. A warm room or summer pushes it up; a failed heater lets it fall.',
      next: {
        default:
          'Keep it steady. A small swing between day and night is normal. Large or fast changes stress fish and corals, and in a reef, corals kept too warm can bleach. A heater controller guards against a heater that sticks on.',
      },
      fresh: {
        what: 'Temperature sets the pace of everything in the tank: fish metabolism, the microbes, and how much oxygen the water can hold. Warmer water holds less oxygen.',
        next: {
          default:
            'Keep it steady. A small swing between day and night is normal, and large or fast changes stress fish. Different fish come from different waters, so your range is set for your type of tank. A heater controller guards against a heater that sticks on.',
        },
      },
    },
    {
      key: 'ph',
      what: 'pH measures how acidic or basic the water is. Seawater is slightly basic.',
      source:
        'It rises during the day, when lights are on and algae and corals use up carbon dioxide, and falls at night when everything breathes it back out. Carbon dioxide in the room air pulls it down too, so a closed-up house can mean lower pH. Alkalinity is the buffer that keeps it from falling further.',
      next: {
        default:
          'A daily swing between day and night is normal. A lower reading first thing in the morning is normal too, because carbon dioxide builds up overnight. Test at the same time of day to compare readings fairly.',
      },
      fresh: {
        what: 'pH measures how acidic or basic the water is. The right value depends on where your fish come from.',
        source:
          'Your source water sets the starting point. Driftwood, peat and injected carbon dioxide lower it; rock such as limestone raises it. The tank slowly becomes more acidic as waste breaks down, and water changes bring it back.',
        next: {
          default:
            'Fish handle a steady pH better than sudden changes, so move it slowly. African cichlids come from hard, high-pH lakes, while many tetras and some shrimp come from softer, more acidic water, which is why your range is set for your type of tank.',
        },
      },
    },
    {
      key: 'kh',
      only: 'fresh',
      what: 'KH, carbonate hardness, is the buffer that holds pH steady in freshwater. Low KH lets pH swing.',
      source:
        'Your source water sets it. The tank slowly uses it up as waste breaks down, and driftwood, peat and active soils lower it. Water changes put it back.',
      next: {
        default:
          'Keep it steady in your range. Some shrimp, such as Caridina, are kept at a KH near zero, while African cichlids need it high, so the right number depends on what you keep.',
      },
    },
    {
      key: 'gh',
      only: 'fresh',
      what: 'GH, general hardness, is the calcium and magnesium dissolved in the water. Shrimp and snails need it to build their shells, and plants use both.',
      source:
        'Your source water sets it. Remineralising salts raise it when you start from RO water, and water changes bring it back toward your source water.',
      next: {
        default:
          'Keep it steady in your range. Shrimp can have trouble moulting when GH is too low or changes quickly.',
      },
    },
    {
      key: 'tds',
      only: 'fresh',
      what: 'TDS, total dissolved solids, is everything dissolved in the water, shown as one number. A TDS pen estimates it from how well the water conducts electricity.',
      source:
        'It rises as water evaporates and as minerals, fertilisers and food are added. Water changes with RO water lower it.',
      next: {
        default:
          'Shrimp keepers use it to make new water match the tank, so a water change does not shock the shrimp. Steady matters more than the exact number.',
      },
    },
  ],
};

function itemsOf(cfg) {
  return Array.isArray(cfg?.items) ? cfg.items : DEFAULT_PARAM_EXPLAINERS.items;
}

function pick(obj, keys) {
  const out = {};
  for (const k of keys) if (obj && obj[k] != null) out[k] = obj[k];
  return out;
}

// The explainer for one param on one tank, or null when there is none (a
// custom param the keeper named themselves, or one meant for the other
// water type). A tank with no subtype is treated as saltwater, the base.
export function explainParam(key, { phase = null, subtype = null, cfg = null } = {}) {
  const item = itemsOf(cfg).find((it) => it?.key === key);
  if (!item) return null;
  const isFresh = FRESHWATER_SUBTYPES.includes(subtype);
  if (item.only === 'fresh' && !isFresh) return null;
  if (item.only === 'salt' && isFresh) return null;
  const fresh = isFresh ? item.fresh || {} : {};
  const next = isFresh
    ? { ...pick(item.next, [...SHARED_PHASES, 'default']), ...(fresh.next || {}) }
    : { ...(item.next || {}) };
  const chain = [phase, phase === 'setup' ? 'cycling' : null, 'default'].filter(Boolean);
  const nextText = chain.map((p) => next[p]).find((t) => typeof t === 'string' && t.trim());
  return {
    key,
    what: fresh.what || item.what,
    source: fresh.source || item.source,
    next: nextText || null,
  };
}

function validText(v) {
  return typeof v === 'string' && v.trim().length > 0;
}

function validNext(next) {
  if (!next || typeof next !== 'object' || Array.isArray(next)) return false;
  for (const [k, v] of Object.entries(next)) {
    if (k !== 'default' && !PHASES.includes(k)) return false;
    if (!validText(v)) return false;
  }
  return true;
}

export function validateParamExplainers(v) {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return 'Must be an object.';
  if (!Array.isArray(v.items)) return 'Needs an "items" array.';
  const keys = new Set();
  for (const [i, it] of v.items.entries()) {
    const at = `items[${i}]`;
    if (!it || typeof it !== 'object') return `${at} must be an object.`;
    if (!validText(it.key)) return `${at} needs a key.`;
    if (keys.has(it.key)) return `${at}: key "${it.key}" is used twice.`;
    keys.add(it.key);
    if (it.only != null && it.only !== 'fresh' && it.only !== 'salt')
      return `${at} "only" must be "fresh", "salt" or left out.`;
    if (!validText(it.what)) return `${at} needs "what".`;
    if (!validText(it.source)) return `${at} needs "source".`;
    if (!validNext(it.next) || !validText(it.next.default))
      return `${at} "next" needs a "default" and only phase keys (${PHASES.join(', ')}).`;
    if (it.fresh != null) {
      if (typeof it.fresh !== 'object' || Array.isArray(it.fresh))
        return `${at} "fresh" must be an object.`;
      if (it.fresh.what != null && !validText(it.fresh.what))
        return `${at} "fresh.what" must be text.`;
      if (it.fresh.source != null && !validText(it.fresh.source))
        return `${at} "fresh.source" must be text.`;
      if (it.fresh.next != null && (!validNext(it.fresh.next) || !validText(it.fresh.next.default)))
        return `${at} "fresh.next" needs a "default" and only phase keys.`;
    }
  }
  return null;
}
